import { firstValueFrom } from 'rxjs';
import {
  ScoButtonModule,
  ScoHeaderModule,
  ScoNavLinkModule,
  ScoDropdownModule,
  ScoDropdownNavigationModule,
  ScoLoadingIndicatorModule,
  ScoMenuItem,
  TooltipModule,
  ScoIconButtonModule,
  ScoChipsModule,
} from '@schueco/schueco-ui-components';
import { Component, OnDestroy, OnInit, inject, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DownloadListService } from '@frontend/feature-download-list-dialog';
import { LoginService } from '@frontend/util-client-auth';
import {
  selectCategoryTreeCategory,
  selectCategoryTreeLoaded,
  CategoryTreeEntity,
  initCategoryTree,
} from '@frontend/data-access-category-tree';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription, Subject, of } from 'rxjs';
import { selectCategoryId } from '@frontend/data-access-categories';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { selectLanguage, setLanguage } from '@frontend/data-access-general';
import {
  downloadList,
  downloadListFailure,
  downloadListSuccess,
  cancelDownload,
} from '@frontend/data-access-download-list';
import { API_DATA, CLIENT_DATA } from '@frontend/util-api-data';
import { Router, RouterModule, Params, ActivatedRoute, NavigationEnd, RouterLink } from '@angular/router';
import { LangRouterService, LocalizeRouterPipe } from '@frontend/util-router-history';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { debounceTime, filter, switchMap } from 'rxjs/operators';
import { clearDownloadList, selectDownloadList, DownloadListItem } from '@frontend/data-access-download-list';
import { SearchDocumentsService, SearchDocumentsEntity } from '@frontend/data-access-search';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntil, take, map } from 'rxjs/operators';
import { ErrorPopupComponent } from '@frontend/ui-error-dialog';
import { Dialog } from '@angular/cdk/dialog';
import { LandRoleDialogService } from '@frontend/feature-land-role-dialog';
import { clearLandRole } from '@frontend/data-access-land-role';
import {
  selectUserInfo,
  selectIsSetUserInfo,
  setUserInfo,
  deleteLastViewedDocument,
  loadUserInfo,
} from '@frontend/data-access-user-info';
import { loadLandRole, selectAllCountriesAndRoles } from '@frontend/data-access-land-role';
import { LAND_ROLE_VIEW_KEY, LandRoleView } from '@frontend/data-access-land-role';
import { initWeblinks, selectAllWeblinks, WeblinksEntity } from '@frontend/data-access-weblinks';
import {
  deleteUserSettings,
  deleteUserSettingsSuccess,
  deleteUserSettingsFailure,
  setUserSettings,
} from '@frontend/data-access-user-settings';
import { RouterHistoryService, isDocumentDetailsRoute } from '@frontend/util-router-history';
import { FeatureFlagActiveDirective } from '@frontend/feature-flags';
import { MaintenanceService } from '@frontend/feature-maintenance';
import {
  loadNews,
  setNewsContentSeen,
  selectNews,
  ChangelogEntry,
  NewsCategory,
  setNewsSeenFlag,
  setNewsSeenFlagSuccess,
} from '@frontend/data-access-news';
import { UtilInfoDialogComponent } from '@frontend/util-info-dialog';

export interface SubNavigation {
  title: string;
  link: string;
  id: number;
  has_disclaimer: boolean;
  subLinks?: SubNavigation[];
}

export interface Navigation {
  title: string;
  subLinks: SubNavigation[];
}

@Component({
  selector: 'dc-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ScoButtonModule,
    ScoIconButtonModule,
    ScoHeaderModule,
    ScoNavLinkModule,
    ScoDropdownModule,
    ScoDropdownNavigationModule,
    TranslateModule,
    ScoLoadingIndicatorModule,
    RouterLink,
    LocalizeRouterPipe,
    TooltipModule,
    ScoChipsModule,
    FeatureFlagActiveDirective,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private downloadListService = inject(DownloadListService);
  private loginService = inject(LoginService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private location = inject(Location);
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private actions$ = inject(Actions);
  private dialog = inject(Dialog);
  private landRoleService = inject(LandRoleDialogService);
  private cdr = inject(ChangeDetectorRef);
  private maintenanceService = inject(MaintenanceService);

  // @Input() categoryId?: string;
  // @Input() filterValues?: number[];
  // @Input() limit?: number;
  // @Input() page?: number;
  categoryId?: string;
  filterValues?: number[];
  limit?: number;
  page?: number;

  loggedIn = false;
  isOffline = false;
  isTouchDisplay = true;

  languages: string[] = inject(CLIENT_DATA).supportedLanguages;
  languageMenuItems: ScoMenuItem[] = [];
  weblinksMenuItems: ScoMenuItem[] = [];
  profileMenuItems: ScoMenuItem[] = [];
  helpAndResourcesMenuItems: ScoMenuItem[] = [];
  changeCountryRoleMenuItems: ScoMenuItem[] = [];
  mobileMenuItems: ScoMenuItem[] = [];
  mobileLanguageMenuItems: ScoMenuItem[] = [];
  mobileChangeCountryRoleMenuItems: ScoMenuItem[] = [];
  client = inject(CLIENT_DATA).name;
  apiData = inject(API_DATA);
  private store = inject(Store);
  localizeService = inject(LangRouterService);
  private routerHistoryService = inject(RouterHistoryService);
  private searchDocumentsService = inject(SearchDocumentsService);
  private elementRef = inject(ElementRef);
  private searchInput$ = new Subject<string>();

  userinfo: any | undefined;
  userGivenName: string | null = null;
  userFamilyName: string | null = null;
  navigationTargetItemId: number | null = null;
  navigation: Navigation | undefined;

  categoryTreeSubscription!: Subscription;
  categoryTreeLoadedSubscription!: Subscription;
  categoryIdSubscription!: Subscription;
  languageSubscription!: Subscription;
  weblinksSubscription!: Subscription;

  categoryId$: Observable<number | undefined>;
  language$: Observable<string | null>;
  pimcoreCategoryTree$: Observable<CategoryTreeEntity[]>;
  categoryTreeLoaded$: Observable<boolean>;
  weblinks$: Observable<WeblinksEntity[]>;
  weblinks: WeblinksEntity[] = [];
  links = {
    DATA_PROTECTION: '',
    IMPRINT: '',
    TERMS_AND_CONDITIONS: '',
    FAQ: '',
    INFORMATION: '',
  };
  searchString = '';
  downloadList$: Observable<Array<DownloadListItem> | undefined>;
  downloadListSubscription!: Subscription;
  downloadListCount = 0;
  isMobile = false;
  isTabletOrMobile = false;
  private destroy$ = new Subject<void>();
  isDownloadListActive = false;
  searchPlaceholder = '';
  isSearchPath = false;
  isFavoritesPath = false;
  private landRoleSubscription!: Subscription;
  selectedCountry: string | null = null;
  selectedRole: string | null = null;
  selectedCountryId: string | null = null;
  selectedRoleId: string | null = null;
  standardCountry: string | null = null;
  standardCountryId: string | null = null;
  standardRole: string | null = null;
  standardRoleId: string | null = null;
  private userInfoSubscription!: Subscription;
  userInfoIdentifier: string | null = null;
  showNewsBadge = false;
  // Bleibt true, sobald der News-Dialog einmal geöffnet wurde, bis die App neu geladen wird
  private newsBadgeDismissedUntilReload = false;
  isCategoryTreeLoaded = false;
  private newsChangelogEntries: ChangelogEntry[] = [];
  private newsChangelogSeenAt = 0;
  private newsContentCategories: NewsCategory[] = [];
  private newsContentSeenAt = 0;
  private categoryTree: CategoryTreeEntity[] = [];
  private setUserInfoSubscription!: Subscription;
  private landRoleData: { countries: { id: string; name: string }[]; roles: { id: string; name: string }[] } | null =
    null;
  isChangeLandRoleAllowed = false;
  isLandRoleInitialized = false;
  searchContentTitle: string | null = null;
  searchContentId: number | null = null;
  isSearchWithoutContext = false;
  canGoBackValue = false;
  recentSearchTerms: string[] = [];
  lastViewedDocuments: any[] = [];
  searchSuggestions: SearchDocumentsEntity[] = [];
  isLoadingSuggestions = false;
  currentSort: string | undefined = undefined;

  @ViewChild('headerRef') headerRef: any;

  // --- Pfeiltastennavigation im Such-Dropdown ---
  // Speichert den globalen Index des aktuell per Tastatur markierten Eintrags (-1 = keiner aktiv)
  activeDropdownIndex = -1;

  constructor() {
    this.pimcoreCategoryTree$ = this.store.pipe(select(selectCategoryTreeCategory));
    this.categoryTreeLoaded$ = this.store.pipe(select(selectCategoryTreeLoaded));
    this.categoryId$ = this.store.pipe(select(selectCategoryId));
    this.language$ = this.store.pipe(select(selectLanguage));
    this.downloadList$ = this.store.pipe(select(selectDownloadList));
    this.weblinks$ = this.store.pipe(select(selectAllWeblinks));
  }

  async ngOnInit() {
    this.loggedIn = await this.loginService.isLoggedIn();
    if (this.loggedIn) {
      this.userinfo = await this.loginService.getUserInfo();
      // Store given_name and family_name separately so they don't get overwritten by Store data
      if (this.userinfo?.given_name) {
        this.userGivenName = this.userinfo.given_name;
      }
      if (this.userinfo?.family_name) {
        this.userFamilyName = this.userinfo.family_name;
      }
    }

    // Subscribe to search input changes and fetch suggestions
    this.searchInput$
      .pipe(
        debounceTime(300),
        switchMap((value) => {
          if (!value.trim()) {
            return of(null);
          }
          return this.searchDocumentsService.searchSuggestions(
            value,
            this.translate.currentLang || 'de',
            this.isFavoritesPath ? null : this.searchContentId,
            this.isFavoritesPath ? 'bookmarks' : undefined,
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.searchSuggestions = response?.results ?? [];
          this.isLoadingSuggestions = false;
        },
        error: (err) => {
          console.error('Search suggestions error:', err);
          this.searchSuggestions = [];
          this.isLoadingSuggestions = false;
        },
      });

    // Subscribe to canGoBack state changes
    this.routerHistoryService.canGoBack$.pipe(takeUntil(this.destroy$)).subscribe((canGoBack) => {
      this.canGoBackValue = canGoBack;
      this.cdr.markForCheck();
    });

    // Vor initHeaderWithLanguage() abonnieren, damit auch die Antwort von loadNews (async) verarbeitet wird
    this.store
      .select(selectNews)
      .pipe(takeUntil(this.destroy$))
      .subscribe((news) => {
        const changelogSeenAt = news?.changelog?.seen_at ?? 0;
        const entries = news?.changelog?.entries ?? [];
        this.newsChangelogEntries = entries;
        this.newsChangelogSeenAt = changelogSeenAt;
        this.newsContentCategories = news?.content?.categories ?? [];
        this.newsContentSeenAt = news?.content?.seen_at ?? 0;
        const hasNewChangelogEntry = entries.some((entry) => entry.published_date > changelogSeenAt);
        const hasNewContentCategory = this.newsContentCategories.some(
          (category) => category.date > this.newsContentSeenAt,
        );
        this.showNewsBadge = !this.newsBadgeDismissedUntilReload && (hasNewChangelogEntry || hasNewContentCategory);
      });

    this.initHeaderWithLanguage(this.translate.currentLang || 'de', false);
    this.updateLanguageMenuItems();
    this.updateProfileMenuItems();
    this.updateChangeCountryRoleMenuItems();
    this.updateMobileMenuItems();
    this.updateMobileLanguageMenuItems();
    this.updateMobileChangeCountryRoleMenuItems();

    this.categoryTreeSubscription = this.pimcoreCategoryTree$.subscribe((categoryTree) => {
      this.categoryTree = categoryTree;
      const navData = this.transformCategories(categoryTree);
      this.navigation = navData;
      this.localizeCategoryTreeTitle();
      this.getSearchContentTitle();
    });

    this.categoryTreeLoadedSubscription = this.categoryTreeLoaded$.subscribe((loaded) => {
      this.isCategoryTreeLoaded = loaded;
    });

    this.weblinksSubscription = this.weblinks$.subscribe((weblinks) => {
      this.weblinks = weblinks;
      this.updateWeblinksMenuItems();
    });

    this.translate.onLangChange.subscribe((event) => {
      this.initHeaderWithLanguage(event.lang, true);
    });

    this.checkRouteParameters(this.route.snapshot.queryParams, this.route.snapshot.params);
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      ////Aktuelle Route-Query-Parameter holen
      const queryParams = this.getQueryParams(this.route.root);
      //Aktuelle Route-Parameter categoryId holen
      const routeParams = this.getRouteParams(this.route.root);
      this.checkRouteParameters(queryParams, routeParams);
      if (event.urlAfterRedirects.includes('/search')) {
        this.isSearchPath = true;
        this.isFavoritesPath = false;
      } else {
        this.isSearchPath = false;
      }
      if (event.urlAfterRedirects.includes('/favorites')) {
        this.isFavoritesPath = true;
        this.isSearchPath = false;
      } else {
        this.isFavoritesPath = false;
      }
      this.getSearchContentTitle();
    });

    this.categoryIdSubscription = this.categoryId$.subscribe((id) => {
      if (id) {
        this.categoryId = String(id);
        this.navigationTargetItemId = id;
      } else {
        this.categoryId = undefined;
        this.navigationTargetItemId = null;
        this.checkRouteParameters(this.route.snapshot.queryParams, this.route.snapshot.params);
      }
      this.getSearchContentTitle();
    });

    this.downloadListSubscription = this.downloadList$.subscribe((downloadList) => {
      if (downloadList) {
        this.downloadListCount = downloadList.length;
      } else {
        this.downloadListCount = 0;
      }
    });

    this.checkScreen();
    window.addEventListener('resize', this.checkScreen.bind(this));
    window.addEventListener('blur', this.onWindowBlur.bind(this));

    this.maintenanceService.connectionProblem$.pipe(takeUntil(this.destroy$)).subscribe((isConnectionProblem) => {
      this.isOffline = isConnectionProblem;
      this.cdr.markForCheck();
    });
    this.elementRef.nativeElement.addEventListener('keydown', this.onCaptureKeydown, true);

    // Downloadliste-Start mitbekommen
    this.actions$.pipe(ofType(downloadList), takeUntil(this.destroy$)).subscribe(() => {
      this.isDownloadListActive = true;
    });

    // Downloadliste-Erfolg oder Fehler mitbekommen
    this.actions$
      .pipe(ofType(downloadListSuccess, downloadListFailure, cancelDownload), takeUntil(this.destroy$))
      .subscribe((action) => {
        if (action.type === downloadListFailure.type) {
          //Download fehlgeschlagen!
          const { status, message } = (action as ReturnType<typeof downloadListFailure>).error;
          this.dialog.open(ErrorPopupComponent, {
            width: '450px',
            data: {
              status: status,
              message: message,
            },
          });
        }
        this.isDownloadListActive = false;
      });

    this.landRoleSubscription = this.store.select(selectAllCountriesAndRoles).subscribe((data) => {
      this.landRoleData = data;
      this.updateLandRoleView();
    });

    this.userInfoSubscription = this.store.select(selectUserInfo).subscribe((userInfo) => {
      if (userInfo) {
        this.userinfo = userInfo; // Update this.userinfo with the current state from the store
        this.isChangeLandRoleAllowed = userInfo.permissions.can_override;
        this.userInfoIdentifier = userInfo.account.identifier;
        this.standardCountryId = userInfo.permissions.country.id;
        this.standardCountry = userInfo.permissions.country.name;
        this.standardRole = userInfo.permissions.roles[0].name;
        this.standardRoleId = userInfo.permissions.roles[0].id;
        this.updateLandRoleView();
        this.updateProfileMenuItems();
        this.updateMobileMenuItems();

        // Mark initialization as complete once we have both landRoleData and userInfo
        if (this.landRoleData) {
          this.isLandRoleInitialized = true;
        }

        // Activity-Daten im localStorage speichern
        if (userInfo.activity) {
          const activityData = {
            search_terms: userInfo.activity.search_terms || [],
            last_viewed_documents: userInfo.activity.last_viewed_documents || [],
          };
          localStorage.setItem('user_activity', JSON.stringify(activityData));
          this.recentSearchTerms = activityData.search_terms;
          this.lastViewedDocuments = activityData.last_viewed_documents;
        } else {
          // Fallback: Aus localStorage laden wenn nicht im userInfo
          this.loadActivityFromLocalStorage();
        }
      }
    });

    this.setUserInfoSubscription = this.store.select(selectIsSetUserInfo).subscribe((isSet) => {
      if (!isSet) {
        this.selectedCountry = this.standardCountry;
        this.selectedRole = this.standardRole;
      } else {
        this.updateLandRoleView();
      }
    });
  }

  // Capture-Phase-Listener für Tastaturnavigation im Dropdown.
  // ArrowDown/ArrowUp: hier abgefangen (vor sco-header). Enter: über (searchEntered) → searchEntered().
  private readonly onCaptureKeydown = (event: KeyboardEvent): void => {
    const total = this.totalDropdownItems;
    if (total === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      this.activeDropdownIndex = (this.activeDropdownIndex + 1) % total;
      setTimeout(() => document.querySelector('.dropdown-item--active')?.scrollIntoView({ block: 'nearest' }));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      this.activeDropdownIndex = (this.activeDropdownIndex - 1 + total) % total;
      setTimeout(() => document.querySelector('.dropdown-item--active')?.scrollIntoView({ block: 'nearest' }));
    } else if (event.key === 'Escape') {
      this.activeDropdownIndex = -1;
    }
  };

  // Gibt die Gesamtanzahl aller sichtbaren Dropdown-Einträge zurück
  private get totalDropdownItems(): number {
    let count = 0;
    if (this.searchSuggestions.length > 0) count += this.searchSuggestions.length;
    if (!this.searchString.trim()) {
      if (this.recentSearchTerms?.length > 0) count += this.recentSearchTerms.length;
      if (this.lastViewedDocuments?.length > 0) count += this.lastViewedDocuments.length;
    }
    return count;
  }

  // Berechnet den globalen Index eines Eintrags über alle drei Sektionen hinweg
  // (Suchergebnisse → Letzte Suchbegriffe → Zuletzt angesehene Dokumente)
  getGlobalDropdownIndex(type: 'suggestion' | 'recent' | 'document', localIndex: number): number {
    const suggestionsCount = this.searchSuggestions.length > 0 ? this.searchSuggestions.length : 0;
    if (type === 'suggestion') return localIndex;
    const recentCount =
      !this.searchString.trim() && this.recentSearchTerms?.length > 0 ? this.recentSearchTerms.length : 0;
    if (type === 'recent') return suggestionsCount + localIndex;
    return suggestionsCount + recentCount + localIndex;
  }

  // Führt die Aktion des aktuell markierten Eintrags aus (wie ein Mausklick)
  private executeActiveDropdownItem(): void {
    const idx = this.activeDropdownIndex;
    const suggestionsCount = this.searchSuggestions.length > 0 ? this.searchSuggestions.length : 0;
    const recentCount =
      !this.searchString.trim() && this.recentSearchTerms?.length > 0 ? this.recentSearchTerms.length : 0;
    this.activeDropdownIndex = -1;
    if (idx < suggestionsCount) {
      this.onSuggestionClick(this.searchSuggestions[idx]);
      this.headerRef?.closeSearchDropdown();
    } else if (idx < suggestionsCount + recentCount) {
      this.onRecentSearchClick(this.recentSearchTerms[idx - suggestionsCount]);
      this.headerRef?.closeSearchDropdown();
    } else {
      this.onDocumentClick(this.lastViewedDocuments[idx - suggestionsCount - recentCount]);
      this.headerRef?.closeSearchDropdown();
    }
  }

  checkScreen() {
    this.isMobile = window.innerWidth <= 768;
    this.isTabletOrMobile = window.innerWidth <= 1024;
  }

  onWindowBlur() {
    if (this.headerRef?.closeSearchDropdown) {
      this.headerRef.closeSearchDropdown();
    }
  }

  private getQueryParams(route: ActivatedRoute): any {
    let queryParams = {};
    while (route.firstChild) {
      route = route.firstChild;
      queryParams = { ...queryParams, ...route.snapshot.queryParams };
    }
    return queryParams;
  }

  private getRouteParams(route: ActivatedRoute): any {
    let params = {};
    while (route.firstChild) {
      route = route.firstChild;
      params = { ...params, ...route.snapshot.params };
    }
    return params;
  }

  checkRouteParameters(queryParams: any, routeParams: any) {
    if (this.router.url.includes('/search')) {
      this.isSearchPath = true;
      this.isFavoritesPath = false;
    } else {
      this.isSearchPath = false;
    }
    if (this.router.url.includes('/favorites')) {
      this.isFavoritesPath = true;
      this.isSearchPath = false;
    } else {
      this.isFavoritesPath = false;
    }

    this.categoryId = queryParams['categoryId'];
    if (routeParams && routeParams['categoryId']) {
      this.categoryId = routeParams['categoryId'];
    }
    this.searchString = queryParams['searchTerm'] ?? '';
    this.limit = queryParams['limit'];
    this.page = queryParams['page'];
    const rawFilterValues = queryParams['filterIds'];
    this.filterValues = rawFilterValues
      ? rawFilterValues
          .split(',')
          .map(Number)
          .filter((val: number) => !isNaN(val))
      : [];
    this.navigationTargetItemId = this.categoryId ? Number(this.categoryId) : null;
    this.currentSort = queryParams['sort'] || undefined;
  }

  localizeCategoryTreeTitle() {
    if (this.navigation && this.languages.length != 0) {
      this.navigation.title = this.translate.instant('CATEGORIES');
    }
  }

  ngOnDestroy() {
    if (this.categoryTreeSubscription) {
      this.categoryTreeSubscription.unsubscribe();
    }
    if (this.categoryTreeLoadedSubscription) {
      this.categoryTreeLoadedSubscription.unsubscribe();
    }
    if (this.categoryIdSubscription) {
      this.categoryIdSubscription.unsubscribe();
    }
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
    if (this.downloadListSubscription) {
      this.downloadListSubscription.unsubscribe();
    }
    if (this.weblinksSubscription) {
      this.weblinksSubscription.unsubscribe();
    }
    if (this.landRoleSubscription) {
      this.landRoleSubscription.unsubscribe();
    }
    if (this.userInfoSubscription) {
      this.userInfoSubscription.unsubscribe();
    }
    if (this.setUserInfoSubscription) {
      this.setUserInfoSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('resize', this.checkScreen.bind(this));
    window.removeEventListener('blur', this.onWindowBlur.bind(this));
    this.elementRef.nativeElement.removeEventListener('keydown', this.onCaptureKeydown, true);
  }

  async logout() {
    this.store.dispatch(clearDownloadList());
    this.store.dispatch(clearLandRole());
    try {
      this.loginService.logout();
    } catch (error) {
      console.error('Logout fehlgeschlagen:', error);
    }
    this.loggedIn = await this.loginService.isLoggedIn();
  }

  async login() {
    this.store.dispatch(clearDownloadList());
    this.store.dispatch(clearLandRole());
    localStorage.removeItem('dc_sort_preference');
    if (this.client === 'jansen' || this.client === 'aks') {
      this.router.navigate([this.localizeService.translateRoute('/login') as string], {
        queryParams: { redirectUri: window.location.pathname + window.location.search + window.location.hash },
      });
    } else {
      this.loginService.login();
    }
  }

  selectedNavigationItemLink(link: string) {
    this.router.navigate([link]);
  }

  private findCategoryById(navigationItems: SubNavigation[], id: number): SubNavigation | undefined {
    for (const navigationItem of navigationItems) {
      if (navigationItem.id === id) {
        return navigationItem;
      }
      if (navigationItem.subLinks) {
        const foundSubNav = this.findCategoryById(navigationItem.subLinks, id);
        if (foundSubNav) {
          return foundSubNav;
        }
      }
    }
    return undefined;
  }

  // Funktion, die den JSON in das von "Dropdown Navigation"-Komponente benötigten Format umwandelt
  private transformCategories(categories: CategoryTreeEntity[]): Navigation {
    const subLinks = categories.map((category: CategoryTreeEntity) => {
      return {
        id: category.id,
        title: category.name,
        link: this.localizeService.translateRoute(
          '/categories/' + category.id + (category.has_documents ? '/documents' : ''),
        ) as string,
        has_disclaimer: category.has_disclaimer,
        subLinks:
          category.childcategories && category.childcategories.length > 0
            ? this.transformSubCategories(category.childcategories)
            : undefined,
      };
    });

    const title = 'Kategorien';
    return {
      title: title,
      subLinks: subLinks,
    };
  }

  private transformSubCategories(childcategories: CategoryTreeEntity[]): SubNavigation[] {
    return childcategories.map((subCategory: CategoryTreeEntity) => {
      return {
        id: subCategory.id,
        title: subCategory.name,
        link: this.localizeService.translateRoute(
          '/categories/' + subCategory.id + (subCategory.has_documents ? '/documents' : ''),
        ) as string,
        has_disclaimer: subCategory.has_disclaimer,
        subLinks:
          subCategory.childcategories && subCategory.childcategories.length > 0
            ? this.transformSubCategories(subCategory.childcategories)
            : undefined,
      };
    });
  }

  setLanguage(lang: string) {
    this.store.dispatch(setLanguage({ language: lang }));
    this.store.dispatch(setUserSettings({ key: 'language', value: lang }));
    this.localizeService.changeLanguage(lang);
    this.updateLanguageMenuItems();
    this.updateMobileLanguageMenuItems();
    this.updateMobileMenuItems();
  }

  onLanguageMenuClick(clickId: string) {
    this.setLanguage(clickId);
  }

  get activeLang(): string {
    return this.translate.currentLang;
  }

  goBack(): void {
    // Check if catalog toolbar navigation is active. If so, use history.go to skip all iframe history entries
    if (this.routerHistoryService.isToolbarNavigationActive()) {
      const count = this.routerHistoryService.getToolbarNavigationCount();

      // Reset toolbar navigation flag before going back
      this.routerHistoryService.setToolbarNavigationActive(false);

      // Go back by count + 1 to skip all iframe entries and exit catalog +1 because we need to skip the current page too
      history.go(-(count + 1));
    } else {
      this.location.back();
    }
  }

  canGoBack(): boolean {
    return this.canGoBackValue;
  }

  loadUrls(lang: string) {
    const urlFilePath = `assets/urls/${lang}.json`;
    this.http.get<any>(urlFilePath).subscribe({
      next: (res) => {
        if (res[this.client]) {
          this.links = res[this.client];
        } else {
          console.warn(`Client "${this.client}" nicht in der URL-Datei gefunden`);
        }
      },
      error: () => console.error(`URL-Datei für Sprache ${lang} konnte nicht geladen werden.`),
    });
  }

  openLink(url: string) {
    window.open(url, '_blank');
  }

  /*---------------------------
  ---     Download Dialog   ---
  ----------------------------*/

  async subscribeAndOpenDownloadDialog() {
    try {
      this.downloadListService.openDownloadListDialog();
    } catch (error) {
      console.error('Error loading document list: ', error);
    }
  }

  searchEntered() {
    // Ist ein Dropdown-Eintrag per Pfeiltaste aktiv oder wurde gerade ausgeführt,
    // diesen ausführen statt die Suche zu starten
    if (this.activeDropdownIndex >= 0) {
      this.executeActiveDropdownItem();
      return;
    }

    const trimmedSearch = (this.searchString || '').trim();
    const queryParams: Params = {};
    const link = this.localizeService.translateRoute(this.isFavoritesPath ? '/favorites' : '/search') as string;

    if (this.filterValues?.length && this.isSearchWithoutContext === false) {
      queryParams['filterIds'] = this.filterValues.join(',');
    }
    this.isSearchWithoutContext = false;

    if (this.limit) {
      queryParams['limit'] = this.limit;
    }
    if (this.categoryId) {
      queryParams['categoryId'] = this.categoryId;
    }
    queryParams['searchTerm'] = trimmedSearch;
    queryParams['page'] = 1;

    if (this.currentSort) {
      queryParams['sort'] = this.currentSort;
    }

    if (this.isFavoritesPath) {
      queryParams['searchType'] = 'bookmarks';
    }

    // Suchbegriff zu Activity-Daten hinzufügen
    if (trimmedSearch) {
      this.updateRecentSearchTerms(trimmedSearch);
    }

    this.router.navigate([link], { queryParams });

    // Fokus vom Suchfeld entfernen
    (document.activeElement as HTMLElement)?.blur();

    if (this.headerRef?.closeSearchDropdown) {
      this.headerRef.closeSearchDropdown();
    }
  }

  searchInputChanged(value: string) {
    this.searchString = value;
    this.searchInput$.next(value);
    this.activeDropdownIndex = -1;
    this.isLoadingSuggestions = !!value.trim();
  }

  setNewView() {
    this.landRoleService.openLandRoleDialog(this.selectedCountryId, this.selectedRoleId);
    this.updateProfileMenuItems();
    this.updateChangeCountryRoleMenuItems();
    this.updateMobileMenuItems();
    this.updateMobileChangeCountryRoleMenuItems();
  }

  async setStandardView() {
    // Helper-Funktion, die auf Success oder Failure für einen Key wartet
    const waitForDelete = (key: string) =>
      firstValueFrom(
        this.actions$.pipe(
          ofType(deleteUserSettingsSuccess, deleteUserSettingsFailure),
          filter((action: any) => {
            if (
              action.type === deleteUserSettingsSuccess.type &&
              (action.response?.key === key || action.response === key)
            )
              return true;
            if (action.type === deleteUserSettingsFailure.type && (action.error?.key === key || action.key === key))
              return true;
            return false;
          }),
          take(1),
        ),
      );

    // Zuerst country löschen
    this.store.dispatch(deleteUserSettings({ key: 'country' }));
    const countryResult = await waitForDelete('country');
    if (countryResult.type === deleteUserSettingsFailure.type) {
      console.error('UserSetting country konnte nicht gelöscht werden:', countryResult.error);
    }

    // Dann role löschen
    this.store.dispatch(deleteUserSettings({ key: 'role' }));
    const roleResult = await waitForDelete('role');
    if (roleResult.type === deleteUserSettingsFailure.type) {
      console.error('UserSetting role konnte nicht gelöscht werden:', roleResult.error);
    }

    this.store.dispatch(clearLandRole());
    this.store.dispatch(setUserInfo({ isSet: false }));
    window.location.reload();
  }

  get isCustomViewActive(): boolean {
    // Don't show indicator until initialization is complete
    if (!this.isLandRoleInitialized) {
      return false;
    }

    // Prüfen, ob sich die ausgewählten Werte von den Standardwerten unterscheiden
    const countryChanged = this.selectedCountryId !== null && this.selectedCountryId !== this.standardCountryId;
    const roleChanged = this.selectedRoleId !== null && this.selectedRoleId !== this.standardRoleId;
    return countryChanged || roleChanged;
  }

  initHeaderWithLanguage(lang: string, isLanguageChange = false) {
    this.loadUrls(lang);
    this.store.dispatch(initCategoryTree());
    if (this.loggedIn) {
      this.store.dispatch(loadLandRole({ language: lang }));
      this.store.dispatch(initWeblinks({ language: lang }));

      // Bei Sprachwechsel: loadUserInfo immer aufrufen
      // Bei Initialisierung: loadUserInfo nur aufrufen, wenn nicht auf Detailseite
      if (isLanguageChange) {
        this.store.dispatch(loadUserInfo({ language: lang }));
      } else if (!isDocumentDetailsRoute(this.router.url)) {
        this.store.dispatch(loadUserInfo({ language: lang }));
        this.store.dispatch(loadNews({ language: lang }));
      }
    }
    this.updateLanguageMenuItems();
    this.updateProfileMenuItems();
    this.updateChangeCountryRoleMenuItems();
    this.updateMobileMenuItems();
    this.updateMobileLanguageMenuItems();
    this.updateMobileChangeCountryRoleMenuItems();
  }

  updateLandRoleView() {
    this.selectedCountry = this.standardCountry;
    this.selectedRole = this.standardRole;
    this.selectedCountryId = this.standardCountryId;
    this.selectedRoleId = this.standardRoleId;

    if (!this.landRoleData) {
      this.updateProfileMenuItems();
      this.updateMobileMenuItems();
      this.updateChangeCountryRoleMenuItems();
      this.updateMobileChangeCountryRoleMenuItems();
      return;
    }

    let countryId: string | null = null;
    let roleId: string | null = null;

    const stored = localStorage.getItem(LAND_ROLE_VIEW_KEY);
    if (stored) {
      try {
        const parsed: LandRoleView = JSON.parse(stored);
        countryId = parsed.countryId;
        roleId = parsed.roleId;
      } catch (e) {
        console.warn('Invalid landRoleView JSON:', e);
      }
    }

    if (countryId && roleId) {
      const validCountry = this.landRoleData.countries.find((c) => c.id === countryId);
      const validRole = this.landRoleData.roles.find((r) => r.id === roleId);
      this.selectedCountry = validCountry ? validCountry.name : null;
      this.selectedRole = validRole ? validRole.name : null;
      this.selectedCountryId = countryId;
      this.selectedRoleId = roleId;
    }

    // Mark initialization as complete if we have userInfo
    if (this.standardCountryId && this.standardRoleId) {
      this.isLandRoleInitialized = true;
    }

    this.updateProfileMenuItems();
    this.updateMobileMenuItems();
    this.updateChangeCountryRoleMenuItems();
    this.updateMobileChangeCountryRoleMenuItems();
  }

  private getSearchContentTitle() {
    if (this.isFavoritesPath) {
      this.searchContentTitle = this.translate.instant('FAVORITES');
      this.searchContentId = null;
      return;
    }
    if (this.navigation && this.navigationTargetItemId) {
      const categoryTitle = this.findCategoryTitleById(this.navigation.subLinks, this.navigationTargetItemId);
      if (categoryTitle) {
        this.searchContentId = this.navigationTargetItemId;
        this.searchContentTitle = categoryTitle;
        return;
      }
    }
    this.searchContentTitle = null;
    this.searchContentId = null;
  }

  private findCategoryTitleById(subLinks: SubNavigation[], id: number): string | undefined {
    for (const subLink of subLinks) {
      if (subLink.id === id) {
        return subLink.title;
      }
      if (subLink.subLinks) {
        const found = this.findCategoryTitleById(subLink.subLinks, id);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  openFaqLink(link: string) {
    const faqUrl = this.apiData.assetURL + link;
    window.open(faqUrl, '_blank');
  }

  private updateLanguageMenuItems() {
    this.languageMenuItems = this.languages.map((lang) => ({
      type: 'button' as const,
      label: this.translate.instant(`LANGUAGES.${lang}`),
      icon: undefined,
      active: lang === this.activeLang,
      disabled: false,
      clickId: lang,
    }));
  }

  private updateWeblinksMenuItems() {
    this.weblinksMenuItems = this.mapWeblinksToMenuItems(this.weblinks);
  }

  private mapWeblinksToMenuItems(weblinks: WeblinksEntity[]): ScoMenuItem[] {
    return weblinks.map((weblink) => {
      if (weblink.type === 'folder') {
        return {
          type: 'group' as const,
          label: weblink.name,
          active: false,
          disabled: false,
          children: weblink.items ? this.mapWeblinksToMenuItems(weblink.items) : [],
        };
      } else {
        return {
          type: 'action' as const,
          label: weblink.name,
          action: () => this.openLink(weblink.url ?? ''),
        };
      }
    });
  }

  onProfileMenuClick(clickId: string) {
    switch (clickId) {
      case 'change-country-role':
        // This is handled by the nested dropdown
        break;
      case 'faq':
        this.openFaqLink(this.links.FAQ);
        break;
      case 'terms':
        this.openLink(this.links.TERMS_AND_CONDITIONS);
        break;
      case 'data-protection':
        this.openLink(this.links.DATA_PROTECTION);
        break;
      case 'imprint':
        this.openLink(this.links.IMPRINT);
        break;
      case 'logout':
        this.logout();
        break;
      case 'login':
        this.login();
        break;
    }
  }

  onMobileMenuClick(clickId: string) {
    switch (clickId) {
      case 'languages':
        // This is handled by the nested dropdown
        break;
      case 'change-country-role':
        // This is handled by the nested dropdown
        break;
      case 'faq':
        this.openLink(this.links.FAQ);
        break;
      case 'terms':
        this.openLink(this.links.TERMS_AND_CONDITIONS);
        break;
      case 'data-protection':
        this.openLink(this.links.DATA_PROTECTION);
        break;
      case 'imprint':
        this.openLink(this.links.IMPRINT);
        break;
      case 'logout':
        this.logout();
        break;
      case 'login':
        this.login();
        break;
    }
  }

  onMobileLanguageMenuClick(clickId: string) {
    this.setLanguage(clickId);
  }

  private updateProfileMenuItems() {
    const items: ScoMenuItem[] = [];

    // Add user info block if logged in
    if (this.loggedIn && this.userInfoIdentifier) {
      items.push({
        type: 'info' as const,
        label: this.userInfoIdentifier,
        subLabel:
          this.selectedCountry && this.selectedRole ? `${this.selectedCountry} | ${this.selectedRole}` : undefined,
        icon: undefined,
      });
    }

    // Add change country role if allowed
    if (this.loggedIn && this.isChangeLandRoleAllowed) {
      items.push({
        type: 'group' as const,
        label: this.translate.instant('CHANGE_COUNTRY_ROLE'),
        icon: 'bi bi-arrow-left-right',
        active: this.isCustomViewActive,
        disabled: false,
        children: this.changeCountryRoleMenuItems,
      });
    }

    // Add login/logout based on status
    if (this.loggedIn) {
      items.push({
        type: 'button' as const,
        label: this.translate.instant('LOGOUT'),
        icon: 'bi bi-box-arrow-right',
        active: false,
        disabled: false,
        clickId: 'logout',
      });
    } else {
      items.push({
        type: 'button' as const,
        label: this.translate.instant('LOGIN'),
        icon: 'bi bi-box-arrow-in-right',
        active: false,
        disabled: false,
        clickId: 'login',
      });
    }

    this.profileMenuItems = items;

    this.updateHelpAndResourcesMenuItems();
  }

  private updateChangeCountryRoleMenuItems() {
    this.changeCountryRoleMenuItems = [
      {
        type: 'action' as const,
        label: this.translate.instant('STANDARD_VIEW'),
        icon: 'bi bi-house-door',
        active: false,
        disabled: false,
        action: () => this.setStandardView(),
      },
      {
        type: 'action' as const,
        label: this.translate.instant('NEW_VIEW'),
        icon: 'bi bi-people',
        active: this.isCustomViewActive,
        disabled: false,
        action: () => this.setNewView(),
      },
    ];
  }

  private updateMobileMenuItems() {
    const items: ScoMenuItem[] = [];

    // Add user info block if logged in
    if (this.loggedIn && this.userInfoIdentifier) {
      items.push({
        type: 'info' as const,
        label: this.userInfoIdentifier,
        subLabel:
          this.selectedCountry && this.selectedRole ? `${this.selectedCountry} | ${this.selectedRole}` : undefined,
        icon: undefined,
      });
    }

    // Add language dropdown
    items.push({
      type: 'group' as const,
      label: this.translate.instant(`LANGUAGES.${this.activeLang}`),
      icon: 'bi bi-globe-americas',
      active: false,
      disabled: false,
      children: this.mobileLanguageMenuItems,
    });

    this.updateHelpAndResourcesMenuItems();
    items.push({
      type: 'group' as const,
      label: this.translate.instant('HELP_AND_RESOURCES'),
      icon: 'bi bi-question-circle',
      active: false,
      disabled: false,
      children: this.helpAndResourcesMenuItems,
    });

    // Add change country role if allowed
    if (this.loggedIn && this.isChangeLandRoleAllowed) {
      items.push({
        type: 'group' as const,
        label: this.translate.instant('CHANGE_COUNTRY_ROLE'),
        icon: 'bi bi-arrow-left-right',
        active: this.isCustomViewActive,
        disabled: false,
        children: this.changeCountryRoleMenuItems,
      });
    }

    // Add login/logout based on status
    if (this.loggedIn) {
      items.push({
        type: 'button' as const,
        label: this.translate.instant('LOGOUT'),
        icon: 'bi bi-box-arrow-right',
        active: false,
        disabled: false,
        clickId: 'logout',
      });
    } else {
      items.push({
        type: 'button' as const,
        label: this.translate.instant('LOGIN'),
        icon: 'bi bi-box-arrow-in-right',
        active: false,
        disabled: false,
        clickId: 'login',
      });
    }

    this.mobileMenuItems = items;
  }

  private updateHelpAndResourcesMenuItems() {
    this.helpAndResourcesMenuItems = [
      {
        type: 'button' as const,
        label: this.translate.instant('FAQ'),
        icon: 'bi bi-question-circle',
        active: false,
        disabled: false,
        clickId: 'faq',
      },
      { type: 'divider' as const },
      {
        type: 'button' as const,
        label: this.translate.instant('FOOTER.TERMS_AND_CONDITIONS'),
        icon: 'bi bi-journal-check',
        active: false,
        disabled: false,
        clickId: 'terms',
      },
      {
        type: 'button' as const,
        label: this.translate.instant('FOOTER.DATA_PROTECTION'),
        icon: 'bi bi-shield-lock',
        active: false,
        disabled: false,
        clickId: 'data-protection',
      },
      {
        type: 'button' as const,
        label: this.translate.instant('FOOTER.IMPRINT'),
        icon: 'bi bi-file-text',
        active: false,
        disabled: false,
        clickId: 'imprint',
      },
    ];
  }

  private updateMobileLanguageMenuItems() {
    this.mobileLanguageMenuItems = this.languages.map((lang) => ({
      type: 'action' as const,
      label: this.translate.instant(`LANGUAGES.${lang}`),
      icon: undefined,
      active: lang === this.activeLang,
      disabled: false,
      action: () => this.setLanguage(lang),
    }));
  }

  private updateMobileChangeCountryRoleMenuItems() {
    this.mobileChangeCountryRoleMenuItems = [
      {
        type: 'action' as const,
        label: this.translate.instant('STANDARD_VIEW'),
        icon: 'bi bi-house-door',
        active: false,
        disabled: false,
        action: () => this.setStandardView(),
      },
      {
        type: 'action' as const,
        label: this.translate.instant('NEW_VIEW'),
        icon: 'bi bi-people',
        active: this.isCustomViewActive,
        disabled: false,
        action: () => this.setNewView(),
      },
    ];
  }

  get hasNavigationLinks(): boolean {
    return !!this.navigation?.subLinks?.length;
  }

  get hasActivityData(): boolean {
    return (
      (this.recentSearchTerms && this.recentSearchTerms.length > 0) ||
      (this.lastViewedDocuments && this.lastViewedDocuments.length > 0)
    );
  }

  openExternalLink() {
    if (this.client === 'aks') {
      window.open('https://my.matterport.com/show/?m=FTTwA3KsbTs', '_blank');
      return;
    }
    if (this.standardCountryId === 'IT') {
      window.open('https://ausschreiben.de/catalog/schueco_it_es', '_blank');
      return;
    }
    window.open('https://ausschreiben.de/catalog/schueco_international', '_blank');
  }

  clearSearchContext() {
    this.searchContentTitle = null;
    this.searchContentId = null;
    this.categoryId = undefined;
    this.isFavoritesPath = false;
    this.isSearchWithoutContext = true;
    this.searchSuggestions = [];
    if (this.searchString.trim()) {
      this.isLoadingSuggestions = true;
      this.searchInput$.next(this.searchString);
      setTimeout(() => {
        if (isDocumentDetailsRoute(this.router.url)) return;
        const input = this.elementRef.nativeElement.querySelector('sco-search input');
        input?.focus();
      });
    }
    if (this.isSearchPath) {
      this.searchEntered();
    }
  }

  clearSearchInput() {
    this.searchString = '';
    this.searchSuggestions = [];
    if (this.isSearchPath || this.isFavoritesPath) {
      this.searchEntered();
    }
  }

  onSuggestionClick(result: SearchDocumentsEntity) {
    const categoryId = result.category_ids?.[0];
    if (categoryId) {
      const trimmedSearch = (this.searchString || '').trim();
      this.router.navigate(
        [
          this.localizeService.translateRoute(
            `/categories/${categoryId}/documents/${result.id}/${this.translate.currentLang || 'de'}`,
          ) as string,
        ],
        trimmedSearch ? { queryParams: { searchTerm: trimmedSearch } } : {},
      );
    }
  }

  openFavorites() {
    const queryParams: Params = {};
    const link = this.localizeService.translateRoute('/favorites') as string;

    queryParams['limit'] = null;
    queryParams['categoryId'] = null;
    queryParams['searchTerm'] = '';
    queryParams['page'] = 1;
    queryParams['filterIds'] = '';
    queryParams['searchType'] = 'bookmarks';

    this.router.navigate([link], { queryParams });
  }

  isFavoritesPathActive(): boolean {
    return this.router.url.includes('/favorites');
  }

  loadActivityFromLocalStorage() {
    try {
      const stored = localStorage.getItem('user_activity');
      if (stored) {
        const activityData = JSON.parse(stored);
        this.recentSearchTerms = activityData.search_terms || [];
        this.lastViewedDocuments = activityData.last_viewed_documents || [];
      }
    } catch (error) {
      console.error('Fehler beim Laden der Activity-Daten:', error);
    }
  }

  onRecentSearchClick(searchTerm: string) {
    this.searchString = searchTerm;
    this.searchEntered();
  }

  onDocumentClick(document: any) {
    if (document.categories && document.categories.length > 0) {
      const categoryId = document.categories[0];
      this.router.navigate([
        this.localizeService.translateRoute(`/categories/${categoryId}/documents/${document.id}/de`) as string,
      ]);
    }
  }

  private updateRecentSearchTerms(searchTerm: string) {
    try {
      // Aktuellen Stand aus localStorage holen
      const stored = localStorage.getItem('user_activity');
      let activityData = {
        search_terms: [] as string[],
        last_viewed_documents: [] as any[],
      };

      if (stored) {
        activityData = JSON.parse(stored);
        if (!activityData.search_terms) {
          activityData.search_terms = [];
        }
        if (!activityData.last_viewed_documents) {
          activityData.last_viewed_documents = [];
        }
      }

      // Duplikate entfernen (falls vorhanden)
      activityData.search_terms = activityData.search_terms.filter((term) => term !== searchTerm);

      // Neuen Suchbegriff am Anfang hinzufügen
      activityData.search_terms.unshift(searchTerm);

      // Maximale Anzahl auf 5 begrenzen
      activityData.search_terms = activityData.search_terms.slice(0, 5);

      // Zurück ins localStorage schreiben
      localStorage.setItem('user_activity', JSON.stringify(activityData));

      // Auch das lokale Array aktualisieren
      this.recentSearchTerms = activityData.search_terms;
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Suchbegriffe:', error);
    }
  }

  removeLastViewedDocument(document: any, event: Event) {
    // Event-Bubbling stoppen, damit nicht das parent element geklickt wird
    event.stopPropagation();

    try {
      // Aktuellen Stand aus localStorage holen
      const stored = localStorage.getItem('user_activity');
      if (!stored) return;

      const activityData = JSON.parse(stored);
      if (!activityData.last_viewed_documents) {
        activityData.last_viewed_documents = [];
      }

      // Dokument aus der Liste entfernen
      activityData.last_viewed_documents = activityData.last_viewed_documents.filter(
        (doc: any) => doc.id !== document.id,
      );

      // Zurück ins localStorage schreiben
      localStorage.setItem('user_activity', JSON.stringify(activityData));

      // Auch das lokale Array aktualisieren
      this.lastViewedDocuments = activityData.last_viewed_documents;

      // Dispatch NgRx Action zum Löschen des Dokuments vom Server
      this.store.dispatch(deleteLastViewedDocument({ documentId: document.id }));
    } catch (error) {
      console.error('Fehler beim Entfernen des Dokuments:', error);
    }
  }

  throwTestError() {
    throw new Error('Test error from header.component.ts');
  }

  openNewsDialog() {
    this.newsBadgeDismissedUntilReload = true;
    this.showNewsBadge = false;

    const changelogEntries = this.newsChangelogEntries.map((entry) => ({
      id: String(entry.id),
      published_date: String(entry.published_date),
      version: entry.version,
      content_type: entry.content_type,
      title: entry.title,
      content: entry.content,
    }));

    const isMobile = window.innerWidth <= 768;

    this.dialog.open(UtilInfoDialogComponent, {
      width: isMobile ? '100vw' : '80%',
      height: isMobile ? '100vh' : '80%',
      maxWidth: isMobile ? '100vw' : '75rem',
      minWidth: isMobile ? '100vw' : undefined,
      disableClose: true,
      panelClass: isMobile ? 'util-info-dialog-mobile' : undefined,
      data: {
        changelogEntries,
        newsCategories: this.newsContentCategories,
        newsChangelogSeenAt: this.newsChangelogSeenAt,
        newsContentSeenAt: this.newsContentSeenAt,
        categoryTree: this.categoryTree,
        localStorageKey: 'newsDialogLastSeen',
        onLeaveNewsTab: (newsType: 'changelog' | 'content') => {
          this.store.dispatch(setNewsSeenFlag({ newsType }));
        },
        onLeaveNewsDialog: () => {
          this.store.dispatch(loadNews({ language: this.translate.currentLang || 'de' }));
        },
        onCategorySeen: (id: number) => {
          this.store.dispatch(setNewsContentSeen({ id }));
        },
        newsSeenSuccess$: this.actions$.pipe(
          ofType(setNewsSeenFlagSuccess),
          map(({ response }) => response.type),
        ),
      },
    });
  }
}
